import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable, combineLatest, of
} from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';

import { HEADER_SIDE_NAV_MENU_DEALER, HEADER_SIDE_NAV_MENU_MANUFACTURER } from 'app/constants/nav.data';
import { AccountProfile, Branch } from 'graphql/models/generated';
import { DropdownItem } from 'models/dropdown.model';
import { NavItem, NavItemType } from 'models/nav.model';
import { AccountService } from 'shared/services/account.service';
import { ConfirmModalService } from 'shared/services/confirm-modal.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { SessionService } from 'shared/services/session.service';
import { SettingsService } from 'shared/services/settings.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-header-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class HeaderSideNavComponent implements OnInit, OnChanges {
  @Input() hasAccountProfile!: boolean;

  initData = false;

  accountProfile$!: Observable<AccountProfile>;

  menu: NavItem[] = [];

  navItemType = NavItemType;

  dropdownData: Record<string, any> = {
    branches: [] as DropdownItem[]
  };

  currentBranch!: Branch;

  isDealer!: boolean;

  currentSubmenu: number | null = null;

  constructor(
    private confirmModalService: ConfirmModalService,
    private sessionService: SessionService,
    private settingsService: SettingsService,
    private accountService: AccountService,
    private storeService: StoreService,
    private permissions: PermissionsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initNav();
  }

  ngOnChanges(): void {
    if (!this.initData && this.hasAccountProfile) {
      this.initNav();
    }
  }

  private initNav(): void {
    if (!this.hasAccountProfile || this.initData) { return; }

    this.initData = this.hasAccountProfile;

    this.accountProfile$ = this.storeService.getAccountProfile();

    combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.getCurrentAccountBranch()
    ]).pipe(
      mergeMap(([isDealer, currentBranch]: [boolean, Branch]) => {
        this.isDealer = isDealer;
        this.currentBranch = currentBranch;

        if (isDealer) {
          if (this.permissions.canReadBranches()) {
            return this.storeService.getBranches();
          }

          return this.storeService.getAccountId()
            .pipe(
              concatMap((accountId: string) => this.accountService.getAccountBranches(accountId))
            );
        }

        return of([]);
      })
    ).subscribe((branches?: Branch[]) => {
      this.menu = this.setMenuItems(this.isDealer);
      this.setBranchMenuSubitems(branches);
    });
  }

  setMenuItems(isDealer: boolean): NavItem[] {
    if (this.permissions.cannotReadHeaderNavSettings()) {
      if (isDealer) {
        return HEADER_SIDE_NAV_MENU_DEALER
          .filter((item) => item.img !== 'settings');
      }

      return HEADER_SIDE_NAV_MENU_MANUFACTURER
        .filter((item) => item.route !== '/einstellungen/unternehmen');
    }

    if (isDealer) {
      if (this.permissions.cannotManageBranch()) {
        const settingsIndex = HEADER_SIDE_NAV_MENU_DEALER.findIndex((item) => item.route === '/einstellungen');
        const subItems = settingsIndex !== undefined ? HEADER_SIDE_NAV_MENU_DEALER[settingsIndex].subItems : [];
        const filteredSubItems = subItems.filter((subItem) => subItem.route !== '/einstellungen/niederlassungen');

        HEADER_SIDE_NAV_MENU_DEALER[settingsIndex].subItems = filteredSubItems;

        return HEADER_SIDE_NAV_MENU_DEALER;
      }

      return HEADER_SIDE_NAV_MENU_DEALER;
    }

    if (this.permissions.cannotReadBranch()) {
      return HEADER_SIDE_NAV_MENU_MANUFACTURER
        .filter((item) => item.route !== '/einstellungen/unternehmen');
    }

    return HEADER_SIDE_NAV_MENU_MANUFACTURER;
  }

  setBranchMenuSubitems(branches?: Branch[]): void {
    if (!branches || branches.length === 0) return;

    const menuItem = this.menu.find((t) => t.title === this.navItemType.BRANCH);

    if (!menuItem) return;

    menuItem.subItems = branches.map((branch) => ({ title: branch.name, value: branch.id }));
  }

  toggleBranch(selectedBranch: DropdownItem): void {
    this.toggleSubMenu(null);
    this.settingsService.getBranch(selectedBranch.value)
      .subscribe((branch: Branch) => {
        this.storeService.setCurrentAccountBranch(branch);
        if (this.router.url === '/angebote' || this.router.url.startsWith('/offer/')) {
          this.router.navigate(['/']);
        }
      });
  }

  toggleSubMenu(index: number | null) {
    this.currentSubmenu = index;
  }

  logout(): void {
    this.confirmModalService
      .show('LOGOUT')
      .onConfirm()
      .pipe(concatMap(() => this.sessionService.logout()))
      .subscribe({
        next: (res: any) => {
          console.info('logout.success', res);
        },
        error: (error: any) => {
          console.error('logout.error', error);
        }
      });
  }
}
